import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Slider } from "@/components/ui/slider";
import TutorialPromptModal from "@/components/TutorialPromptModal";
import DiscoverTutorialSlides from "@/components/DiscoverTutorialSlides";

const FilterHint = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const closingRef = React.useRef(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<"above" | "below">("above");
  const [alignStyle, setAlignStyle] = useState<React.CSSProperties>({});

  React.useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const tooltipW = 224; // w-56 = 14rem = 224px
    // Horizontal: try to center, but clamp to viewport
    let left = rect.left + rect.width / 2 - tooltipW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipW - 8));
    const fixedLeft = left;
    // Vertical: prefer above, fall back to below
    if (rect.top > 80) {
      setPos("above");
      setAlignStyle({ position: "fixed", left: fixedLeft, top: rect.top - 8, transform: "translateY(-100%)" });
    } else {
      setPos("below");
      setAlignStyle({ position: "fixed", left: fixedLeft, top: rect.bottom + 8 });
    }
  }, [open]);

  return (
    <span className="relative inline-flex ml-1.5 align-middle" onClick={(e) => e.preventDefault()}>
      <button ref={btnRef} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (closingRef.current) { closingRef.current = false; return; } setOpen((v) => !v); }} className="w-4 h-4 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors" aria-label="Info">
        <Info className="w-3 h-3 text-muted-foreground" />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40 cursor-default" onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); closingRef.current = true; setOpen(false); }} style={{ overflow: 'hidden' }} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }} style={alignStyle} className="z-50 w-56 p-2.5 rounded-lg bg-card border border-border shadow-card text-xs text-foreground leading-relaxed">
              {text}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </span>
  );
};

const movieGenres = ["Action", "Adventure", "Animation", "Comedy", "Crime", "Documentary", "Drama", "Family", "Fantasy", "History", "Horror", "Music", "Mystery", "Romance", "Sci-Fi", "Thriller", "War", "Western"];
const tvGenres = ["Action & Adventure", "Animation", "Comedy", "Crime", "Documentary", "Drama", "Family", "History", "Horror", "Kids", "Music", "Mystery", "Reality", "Romance", "Sci-Fi & Fantasy", "Soap", "Talk", "Thriller", "War & Politics", "Western"];

export interface DiscoverSettings {
  mediaType: "movie" | "tv"; maxRuntime: number; decisionTimer: number;
  genres: string[]; decades: string[]; includeBookmarks: boolean;
  includeCurrentlyWatching: boolean;
}

interface DiscoverSetupProps { onStart: (settings: DiscoverSettings) => void; onGroupMode?: () => void; }

const DiscoverSetup = ({ onStart, onGroupMode }: DiscoverSetupProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState<"pick" | "solo">(onGroupMode ? "pick" : "solo");

  // Tutorial state
  const [tutorialPromptType, setTutorialPromptType] = useState<"solo" | "group" | null>(null);
  const [showTutorial, setShowTutorial] = useState<"solo" | "group" | null>(null);
  const [mediaType, setMediaType] = useState<"movie" | "tv">("movie");
  const [maxRuntime, setMaxRuntime] = useState(90);
  const [decisionTimer, setDecisionTimer] = useState(10);
  const [selectedGenres, setSelectedGenres] = useState<string[]>(["any"]);
  const [selectedDecades, setSelectedDecades] = useState<string[]>(["any"]);
  
  const [includeBookmarks, setIncludeBookmarks] = useState(false);
  const [includeCurrentlyWatching, setIncludeCurrentlyWatching] = useState(false);

  const toggleGenre = (g: string) => {
    if (g === "any") { setSelectedGenres(["any"]); } else {
      setSelectedGenres((prev) => { const without = prev.filter((x) => x !== "any"); const next = without.includes(g) ? without.filter((x) => x !== g) : [...without, g]; return next.length === 0 ? ["any"] : next; });
    }
  };

  const runtimeLabel = maxRuntime >= 180 ? t("runtimeLabels.threeHoursPlus") : (maxRuntime % 60 > 0 ? t("runtimeLabels.hourMin", { h: Math.floor(maxRuntime / 60), m: maxRuntime % 60 }) : t("runtimeLabels.hourOnly", { h: Math.floor(maxRuntime / 60) }));

  const isTutorialDismissed = (type: "solo" | "group") =>
    localStorage.getItem(`ayeris_tutorial_dismissed_${type}`) === "true";

  const dismissTutorial = (type: "solo" | "group") =>
    localStorage.setItem(`ayeris_tutorial_dismissed_${type}`, "true");

  const handleSessionTypeClick = (type: "solo" | "group") => {
    if (isTutorialDismissed(type)) {
      if (type === "solo") setMode("solo");
      else onGroupMode?.();
    } else {
      setTutorialPromptType(type);
    }
  };

  // Show full tutorial slides
  if (showTutorial) {
    return (
      <DiscoverTutorialSlides
        sessionType={showTutorial}
        onComplete={() => {
          const type = showTutorial;
          setShowTutorial(null);
          if (type === "solo") setMode("solo");
          else onGroupMode?.();
        }}
      />
    );
  }

  if (mode === "pick") {
    return (
      <div className="h-[100dvh] bg-background flex items-center justify-center px-4 py-4">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/home")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"><ArrowLeft className="w-4 h-4" />{t("common.back")}</button>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-6">
            <div className="text-center">
              <h2 className="font-display text-2xl font-bold text-foreground">{t("discover.whatsTheVibe")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("discover.howToDecide")}</p>
            </div>
            <div className="space-y-3">
              <button onClick={() => handleSessionTypeClick("solo")} className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 transition-all active:scale-[0.98]">
                <span className="text-4xl">🍿</span>
                <div className="text-left"><p className="font-display font-bold text-foreground text-lg">{t("discover.soloSession")}</p><p className="text-xs text-muted-foreground">{t("discover.soloDesc")}</p></div>
              </button>
              <button onClick={() => handleSessionTypeClick("group")} className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 transition-all active:scale-[0.98]">
                <span className="text-4xl">👥</span>
                <div className="text-left"><p className="font-display font-bold text-foreground text-lg">{t("discover.groupSession")}</p><p className="text-xs text-muted-foreground">{t("discover.groupDesc")}</p></div>
              </button>
            </div>
          </motion.div>
        </div>

        <TutorialPromptModal
          open={!!tutorialPromptType}
          sessionType={tutorialPromptType || "solo"}
          onYes={() => {
            const type = tutorialPromptType!;
            setTutorialPromptType(null);
            setShowTutorial(type);
          }}
          onNo={() => {
            const type = tutorialPromptType!;
            setTutorialPromptType(null);
            if (type === "solo") setMode("solo");
            else onGroupMode?.();
          }}
          onDismissForever={() => {
            if (tutorialPromptType) dismissTutorial(tutorialPromptType);
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-background flex flex-col px-4 py-4 pb-16">
      <div className="w-full max-w-md mx-auto flex flex-col flex-1 min-h-0">
        {!onGroupMode && (<button onClick={() => navigate("/home")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3 self-start"><ArrowLeft className="w-4 h-4" />{t("common.back")}</button>)}
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full bg-gradient-card border border-border rounded-2xl shadow-card flex flex-col overflow-hidden flex-1 min-h-0">
          <div className="text-center pt-5 px-5 pb-2 shrink-0">
            <h2 className="font-display text-xl font-bold text-foreground">{t("discover.whatsTheVibe")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("discover.setupSolo")}</p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 pb-2 flex flex-col justify-evenly gap-3">
            <div className="flex gap-2">
              {([{ type: "movie" as const, emoji: "🎬", label: t("common.movie") }, { type: "tv" as const, emoji: "📺", label: t("common.tvShow") }]).map((opt) => (
                <button key={opt.type} onClick={() => { setMediaType(opt.type); setSelectedGenres(["any"]); }} className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 transition-all active:scale-95 ${mediaType === opt.type ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-muted-foreground/30"}`}>
                  <span className="text-lg">{opt.emoji}</span><span className={`text-xs font-medium ${mediaType === opt.type ? "text-primary" : "text-muted-foreground"}`}>{opt.label}</span>
                </button>
              ))}
            </div>

            {mediaType === "movie" && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-muted-foreground tracking-wider">{t("discover.howLong")}<FilterHint text={t("discover.runtimeHint")} /></label>
                  <span className="text-sm font-display font-bold text-foreground">{runtimeLabel}</span>
                </div>
                <Slider value={[maxRuntime]} onValueChange={([v]) => setMaxRuntime(v)} min={30} max={180} step={30} />
                <div className="flex justify-between mt-0.5"><span className="text-[10px] text-muted-foreground">30m</span><span className="text-[10px] text-muted-foreground">3h+</span></div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-muted-foreground tracking-wider">{t("discover.decisionTimer")}<FilterHint text={t("discover.decisionTimerHint")} /></label>
                <span className="text-sm font-display font-bold text-foreground">{decisionTimer} min</span>
              </div>
              <Slider value={[decisionTimer]} onValueChange={([v]) => setDecisionTimer(v)} min={5} max={30} step={5} />
              <div className="flex justify-between mt-0.5"><span className="text-[10px] text-muted-foreground">5 min</span><span className="text-[10px] text-muted-foreground">30 min</span></div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground tracking-wider mb-1.5 block">{t("discover.genres")}<FilterHint text={t("discover.genresHint")} /></label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => toggleGenre("any")} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${selectedGenres.includes("any") ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>{t("common.any")}</button>
                {(mediaType === "movie" ? movieGenres : tvGenres).map((genre) => (
                  <button key={genre} onClick={() => toggleGenre(genre)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${selectedGenres.includes(genre) ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>{t("genreLabels." + genre, genre)}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground tracking-wider mb-1.5 block">{t("discover.era")}<FilterHint text={t("discover.eraHint")} /></label>
              <div className="flex flex-wrap gap-1.5">
                {["any", "Last 12 months", "2020s", "2010s", "2000s", "1990s", "1980s", "1970s", "1960s & earlier"].map((decade) => {
                  const decadeKey = decade === "any" ? "any" : decade === "Last 12 months" ? "last12months" : decade === "1960s & earlier" ? "1960sEarlier" : decade;
                  return (
                  <button key={decade} onClick={() => { if (decade === "any") { setSelectedDecades(["any"]); } else { setSelectedDecades((prev) => { const without = prev.filter((d) => d !== "any"); const next = without.includes(decade) ? without.filter((d) => d !== decade) : [...without, decade]; return next.length === 0 ? ["any"] : next; }); } }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${selectedDecades.includes(decade) ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>
                    {t("decadeLabels." + decadeKey, decade)}
                  </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeBookmarks} onChange={(e) => setIncludeBookmarks(e.target.checked)} className="w-3.5 h-3.5 rounded border-border accent-primary" />
                <span className="text-[11px] text-muted-foreground">{t("discover.includeBookmarks")}</span>
                <FilterHint text={t("discover.includeBookmarksHint")} />
              </label>

              {mediaType === "tv" && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={includeCurrentlyWatching} onChange={(e) => setIncludeCurrentlyWatching(e.target.checked)} className="w-3.5 h-3.5 rounded border-border accent-primary" />
                  <span className="text-[11px] text-muted-foreground">{t("discover.includeCurrentlyWatching")}</span>
                  <FilterHint text={t("discover.includeCurrentlyWatchingHint")} />
                </label>
              )}
            </div>
          </div>

          <div className="px-5 py-4 space-y-3 shrink-0 border-t border-border/50">
            <button onClick={() => onStart({ mediaType, maxRuntime, decisionTimer, genres: selectedGenres.includes("any") ? [] : selectedGenres, decades: selectedDecades, includeBookmarks, includeCurrentlyWatching: mediaType === "tv" ? includeCurrentlyWatching : false })} className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-lg shadow-iris active:scale-95 transition-transform">
              {t("discover.letsGo")}
            </button>
            {onGroupMode && (<button onClick={() => setMode("pick")} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">{t("discover.backToSessionType")}</button>)}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DiscoverSetup;
